"use client"

import * as React from "react"
import { format } from "date-fns"
import { fr, enUS } from "date-fns/locale"
import { Calendar as CalendarIcon, X } from "lucide-react"

import { useTranslation } from "react-i18next"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"

interface DatePickerProps {
    /** Identifiant du déclencheur — permet à un <Label htmlFor> de le cibler. */
    id?: string
    value?: Date | null
    onChange?: (date: Date | undefined) => void
    /** Si fourni, affiche une croix « Effacer » à droite quand une date est saisie. */
    onClear?: () => void
    placeholder?: string
    disabled?: boolean
    className?: string
    /** Format d'affichage de la date (défaut: "dd/MM/yyyy") */
    dateFormat?: string
    /** Afficher le bouton Aujourd'hui (défaut: true) */
    showTodayButton?: boolean
    /** Année de début pour le dropdown (défaut: 1920) */
    startYear?: number
    /** Année de fin pour le dropdown (défaut: 2050) */
    endYear?: number
    /** Date maximale sélectionnable (les jours postérieurs sont grisés et non cliquables). */
    maxDate?: Date
    /** Date minimale sélectionnable (les jours antérieurs sont grisés et non cliquables). */
    minDate?: Date
    /** Le jour que désigne le bouton « Aujourd'hui » (défaut : le jour du NAVIGATEUR).
     *  Un formulaire borné au jour de l'ORGANISME le lui passe : sans cela, le
     *  bouton proposait un autre jour que le champ pré-rempli — voire un jour grisé. */
    today?: Date
    /** Nom accessible quand aucun `<Label htmlFor>` ne peut le porter — deux
     *  bornes côte à côte dans une barre d'outils, par exemple. Sans lui, le
     *  lecteur d'écran annonce la DATE AFFICHÉE comme nom du bouton, et les
     *  deux bornes d'une plage deviennent indiscernables. */
    'aria-label'?: string
    /** Identifiant(s) du texte qui DÉCRIT le champ — la consigne de saisie écrite par
     *  l'organisme, un message d'erreur. ⚠️ Ajouté le 2026-09-18 : sans cette prop, une
     *  aide affichée à côté du déclencheur n'était rattachée à RIEN, et le lecteur d'écran
     *  ne la lisait jamais. La date était le seul des six types de champ personnalisé à
     *  ne pas pouvoir la porter — une dimension laissée ouverte parce que la traversée
     *  manquait ici. */
    'aria-describedby'?: string
}

/**
 * DatePicker component with ergonomic navigation.
 * 
 * Features:
 * - Dropdown month/year navigation (for distant dates like birthdates)
 * - "Today" quick-select button with abbreviated date
 * - French locale
 */
export function DatePicker({
    id,
    value,
    onChange,
    onClear,
    placeholder,
    disabled = false,
    className,
    dateFormat = "dd/MM/yyyy",
    showTodayButton = true,
    startYear = 1920,
    endYear = 2050,
    maxDate,
    minDate,
    today: aujourdhuiImpose,
    'aria-label': ariaLabel,
    'aria-describedby': ariaDescribedBy,
}: DatePickerProps) {
    const { t, i18n } = useTranslation()
    const localeMap: Record<string, typeof fr> = { fr, en: enUS }
    const locale = localeMap[i18n.language?.slice(0, 2)] || fr
    const resolvedPlaceholder = placeholder ?? t('components.datePicker.placeholder')
    const [open, setOpen] = React.useState(false)
    const [month, setMonth] = React.useState<Date>(value ?? new Date())

    const today = aujourdhuiImpose ?? new Date()

    const handleSelect = (date: Date | undefined) => {
        onChange?.(date)
        setOpen(false)
    }

    const handleTodayClick = () => {
        onChange?.(today)
        setOpen(false)
    }

    // Format: "Lundi 19 Janvier 2026" (Title Case)
    const todayRaw = format(today, "EEEE d MMMM yyyy", { locale })
    // Capitalize first letter of each space-separated word (day and month)
    // Note: \b\w breaks on accented characters (e.g. "février" → "féVrier")
    const todayFormatted = todayRaw
        .split(" ")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")

    const showClear = Boolean(onClear) && Boolean(value) && !disabled

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <div className="relative">
                <PopoverTrigger asChild>
                    <Button
                        id={id}
                        aria-label={ariaLabel}
                        aria-describedby={ariaDescribedBy}
                        variant="outline"
                        className={cn(
                            "w-full justify-start text-left font-normal",
                            !value && "text-muted-foreground",
                            // La croix « effacer » passe à 44 px sous md: : on
                            // réserve la même largeur, sinon elle recouvrirait
                            // la fin de la date au lieu de vivre à côté d'elle.
                            showClear && "pr-11 md:pr-8",
                            className
                        )}
                        disabled={disabled}
                    >
                        {/* ⚠️ La valeur était un nœud de texte NU et le
                            placeholder un `<span>` sans `truncate` : dans un
                            bouton `whitespace-nowrap`, leur `min-width: auto`
                            vaut la chaîne ENTIÈRE, insécable. Le texte ne
                            pouvait donc pas s'élider — seulement déborder du
                            bouton (mesuré : « Sélectionner une date » réclame
                            ~209 px dans une colonne de 149 px). On recopie ici
                            la recette déjà normalisée du `SelectTrigger`
                            (`select.tsx` : `flex-1 min-w-0 truncate text-left`)
                            : `truncate` pose `overflow: hidden`, ce qui ramène
                            ce plancher à 0 et rend le bouton compressible.
                            `shrink-0` protège l'icône de l'écrasement. */}
                        <CalendarIcon className="mr-2 h-4 w-4 shrink-0" />
                        <span className="min-w-0 flex-1 truncate text-left">
                            {value ? format(value, dateFormat, { locale }) : resolvedPlaceholder}
                        </span>
                    </Button>
                </PopoverTrigger>
                {showClear && (
                    // ⚠️ Cible de 16×16 px : un `<button>` sans rembourrage
                    // autour d'une icône `h-4 w-4`. DS §13.2 l'interdit
                    // nommément (« icône cliquable sans padding suffisant ») et
                    // exige 44 px au doigt. Aggravant : ce bouton est en `z-10`
                    // PAR-DESSUS le déclencheur, donc un doigt qui vise le champ
                    // efface l'échéance au lieu d'ouvrir le calendrier. Sous
                    // md: la boîte passe à 44×44 dans la gouttière réservée par
                    // `pr-11` — elle ne recouvre donc aucun texte ; au-delà, on
                    // republie à l'identique la boîte 16×16 à `right-2`.
                    <button
                        type="button"
                        aria-label={t('common.clear')}
                        className="absolute right-0 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center text-muted-foreground hover:text-foreground md:right-2 md:h-4 md:w-4"
                        onClick={(e) => {
                            e.stopPropagation()
                            onClear?.()
                        }}
                    >
                        <X className="h-4 w-4" />
                    </button>
                )}
            </div>
            <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                    mode="single"
                    selected={value ?? undefined}
                    onSelect={handleSelect}
                    month={month}
                    onMonthChange={setMonth}
                    initialFocus
                    startYear={startYear}
                    endYear={endYear}
                    disabled={
                        maxDate && minDate
                            ? [{ after: maxDate }, { before: minDate }]
                            : maxDate
                              ? { after: maxDate }
                              : minDate
                                ? { before: minDate }
                                : undefined
                    }
                />

                {showTodayButton && (
                    <>
                        <Separator />
                        <div className="p-2">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-full justify-start text-muted-foreground hover:text-foreground"
                                onClick={handleTodayClick}
                            >
                                <CalendarIcon className="mr-2 h-4 w-4" />
                                {todayFormatted}
                            </Button>
                        </div>
                    </>
                )}
            </PopoverContent>
        </Popover>
    )
}
